import React, { useCallback, useEffect, useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosPopover,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, CheckCircle } from "mosaic-react-icons";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

const ACCEPTED_CLOSE_DELAY_MS = 1500;

const formatCellValue = (value) => {
    if (value == null || value === "") return "—";
    return String(value);
};

const ComparisonArrow = () => (
    <SovosBox
        aria-hidden
        sx={{
            "&::after": {
                borderBottom: "5px solid transparent",
                borderLeft: "7px solid",
                borderLeftColor: "text.primary",
                borderTop: "5px solid transparent",
                content: '""',
                height: 0,
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
            },
            bgcolor: "text.primary",
            height: 2,
            position: "relative",
            width: 48,
        }}
    />
);

const ValueCell = ({ label, tone, value }) => {
    const isOriginal = tone === "original";

    return (
        <SovosBox
            sx={{
                alignItems: "center",
                display: "flex",
                flex: 1,
                flexDirection: "column",
                gap: 0.75,
                minWidth: 0,
            }}
        >
            <SovosTypography
                sx={{
                    color: "text.secondary",
                    fontSize: 11,
                    lineHeight: "16px",
                    textTransform: "capitalize",
                }}
                variant="caption"
            >
                {label}
            </SovosTypography>
            <SovosBox
                sx={{
                    alignItems: "center",
                    bgcolor: isOriginal ? "error.light" : "success.light",
                    borderRadius: 1,
                    display: "flex",
                    justifyContent: "center",
                    minHeight: 40,
                    minWidth: 72,
                    px: 1.5,
                    py: 1,
                    width: "100%",
                }}
            >
                <SovosTypography
                    fontWeight={700}
                    noWrap
                    sx={{
                        color: isOriginal ? "error.main" : "success.dark",
                        fontSize: 14,
                        lineHeight: "20px",
                        maxWidth: "100%",
                    }}
                    variant="body2"
                >
                    {formatCellValue(value)}
                </SovosTypography>
            </SovosBox>
        </SovosBox>
    );
};

const ValueComparison = ({ confidence, fromValue, i18n, toValue }) => {
    const confidencePct = confidence != null ? Math.round(confidence * 100) : null;

    return (
        <SovosBox
            sx={{
                alignItems: "flex-end",
                display: "flex",
                gap: 1.5,
                width: "100%",
            }}
        >
            <ValueCell label={i18n`Original value`} tone="original" value={fromValue} />
            <SovosBox
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                    gap: 0.75,
                    pb: 1.75,
                }}
            >
                {confidencePct != null && (
                    <SovosTypography
                        fontWeight={700}
                        sx={{
                            color: "text.primary",
                            fontSize: 11,
                            lineHeight: "16px",
                            whiteSpace: "nowrap",
                        }}
                        variant="caption"
                    >
                        {confidencePct}% {i18n`Confident`}
                    </SovosTypography>
                )}
                <ComparisonArrow />
            </SovosBox>
            <ValueCell label={i18n`new value`} tone="new" value={toValue} />
        </SovosBox>
    );
};

const StagingAutoFixModal = ({
    anchorEl = null,
    onAccept,
    onClose,
    onFinalizeAccept,
    onReject,
    open,
    suggestion = null,
}) => {
    const { i18n } = useGlobalValue();
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        if (!open) {
            setAccepted(false);
        }
    }, [open]);

    const handleAccept = useCallback(async () => {
        if (accepted || !onAccept) return;
        setAccepted(true);
        try {
            const result = await Promise.resolve(onAccept());
            if (result === false) {
                setAccepted(false);
                return;
            }
            window.setTimeout(() => {
                onFinalizeAccept?.();
                onClose?.();
            }, ACCEPTED_CLOSE_DELAY_MS);
        } catch (_error) {
            setAccepted(false);
        }
    }, [accepted, onAccept, onClose, onFinalizeAccept]);

    useEffect(() => {
        if (!open || accepted) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "r" || event.key === "R") {
                event.preventDefault();
                onReject?.();
            }
            if (event.key === "a" || event.key === "A") {
                event.preventDefault();
                if (!suggestion?.requiresReview) {
                    handleAccept();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [accepted, handleAccept, onReject, open]);

    if (!suggestion) return null;

    return (
        <SovosPopover
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            onClose={onClose}
            open={open}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxWidth: 420,
                    overflow: "hidden",
                    width: 420,
                },
            }}
        >
            <SovosBox
                onClick={(event) => event.stopPropagation()}
                sx={{ bgcolor: "common.white" }}
            >
                <SovosBox
                    sx={{
                        alignItems: "center",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1,
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <AutoAwesome sx={{ color: "secondary.main", fontSize: 20 }} />
                    <SovosTypography
                        fontWeight={700}
                        sx={{ color: "text.primary", fontSize: 16, lineHeight: "24px" }}
                        variant="subtitle2"
                    >
                        {i18n`Auto fix by Sovi`}
                    </SovosTypography>
                </SovosBox>

                <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2, py: 2 }}>
                    <ValueComparison
                        confidence={suggestion.confidence}
                        fromValue={suggestion.originalValue}
                        i18n={i18n}
                        toValue={suggestion.newValue}
                    />

                    {suggestion.rationale && (
                        <SovosTypography
                            sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                lineHeight: 1.5,
                            }}
                            variant="body2"
                        >
                            {suggestion.rationale}
                        </SovosTypography>
                    )}

                    {suggestion.requiresReview && (
                        <SovosTypography
                            sx={{
                                color: "warning.dark",
                                fontSize: 12,
                                fontWeight: 600,
                                lineHeight: 1.5,
                            }}
                            variant="body2"
                        >
                            {i18n`Manual correction required`}
                        </SovosTypography>
                    )}
                </SovosBox>

                <SovosBox
                    sx={{
                        alignItems: "center",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <SovosButton
                        disabled={accepted}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onReject?.();
                        }}
                        size="small"
                        sx={{ textTransform: "none" }}
                        type="button"
                        variant="text"
                    >
                        {i18n`Reject (R)`}
                    </SovosButton>
                    {accepted ? (
                        <SovosBox sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
                            <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                            <SovosTypography
                                fontWeight={700}
                                sx={{ color: "text.primary", fontSize: 14, lineHeight: "20px" }}
                                variant="body2"
                            >
                                {i18n`Accepted`}
                            </SovosTypography>
                        </SovosBox>
                    ) : suggestion.requiresReview ? (
                        <SovosTypography
                            sx={{ color: "text.secondary", fontSize: 12 }}
                            variant="body2"
                        >
                            {i18n`Edit the cell directly`}
                        </SovosTypography>
                    ) : (
                        <SovosButton
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleAccept();
                            }}
                            size="small"
                            sx={{ textTransform: "none" }}
                            type="button"
                            variant="contained"
                        >
                            {i18n`Accept (A)`}
                        </SovosButton>
                    )}
                </SovosBox>
            </SovosBox>
        </SovosPopover>
    );
};

export default StagingAutoFixModal;
